import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { formatDateTime } from "@buildpy/core/lib/format";
import { PROPOSAL_REASON_MAX, VOTE_STANCES, type VoteStance } from "@buildpy/core/modules/process-standard/proposal/types";
import type { StandardProposalPageView } from "@buildpy/core/views/g5a/standard-proposals";
import { Button } from "@buildpy/ui/button";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Textarea } from "@buildpy/ui/textarea";

import { voteProposal } from "@/api/actions/process-standard";
import Field from "@/components/field";
import { Link } from "@/lib/nav";

import { ProposalSummary, VOTE_LABEL } from "./proposals-board";
import { canVote, proposalsHref } from "./standards-search";
import { useStandardAction } from "./use-standard-action";


// 찬반 남기기 — 사유 필수 · 한 회사 한 번(최종 판정은 RPC).
export const VoteForm = ({ proposalId }: { proposalId: string }) => {
  const [stance, setStance] = useState<VoteStance | null>(null);
  const [reason, setReason] = useState("");
  const { pending, run } = useStandardAction();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stance) return;
    run(
      () => voteProposal({ proposal_id: proposalId, stance, reason }),
      () => {
        setReason("");
        setStance(null);
        toast.success("의견을 남겼습니다");
      }
    );
  };

  return (
    <form className="flex flex-col gap-y-3" data-testid="vote-form" onSubmit={onSubmit}>
      <fieldset className="flex flex-col gap-y-3" disabled={pending}>
        <div aria-label="찬반" className="flex gap-2" role="group">
          {VOTE_STANCES.map((s) => (
            <Button key={s} aria-pressed={stance === s} onClick={() => setStance(s)} size="chip" type="button" variant="chip">
              {VOTE_LABEL[s]}
            </Button>
          ))}
        </div>
        <Field label="이유 (필수)">
          <Textarea className="min-h-20 text-sm" maxLength={PROPOSAL_REASON_MAX} onChange={(e) => setReason(e.target.value)} value={reason} />
        </Field>
      </fieldset>
      <div className="flex justify-end">
        <Button disabled={pending || stance === null || reason.trim() === ""} size="sm" type="submit">
          의견 남기기
        </Button>
      </div>
    </form>
  );
};

// 왜 찬반을 못 남기는지 — 폼 대신 한 줄.
export const voteClosedText = (p: { review_open: boolean; is_mine: boolean; my_vote: VoteStance | null }): string | null => {
  if (p.is_mine) return "우리 회사 제안에는 찬반을 남기지 않습니다.";
  if (p.my_vote) return `이미 ${VOTE_LABEL[p.my_vote]} 의견을 남겼습니다.`;
  if (!p.review_open) return "검토 기간이 끝났습니다.";
  return null;
};

// 제안 하나 — 바꿀 숫자·이유·찬반 사유 전부(회사 이름 공개, #386).
const ProposalDetail = ({ data }: { data: StandardProposalPageView }) => {
  const back = (
    <Link className="text-sm text-muted-foreground underline-offset-4 hover:underline" href={proposalsHref(null)}>
      ← 제안 목록
    </Link>
  );
  const proposal = data.proposal;
  if (!proposal) {
    return (
      <div className="flex flex-col gap-y-3">
        {back}
        <EmptyState>제안을 찾을 수 없습니다.</EmptyState>
      </div>
    );
  }
  const closed = voteClosedText(proposal);
  return (
    <div className="flex flex-col gap-y-4">
      {back}
      <SectionCard data-testid="proposal-detail">
        <ProposalSummary proposal={proposal} />
        <p className="mt-3 whitespace-pre-line break-words text-sm">{proposal.reason}</p>
        <p className="mt-2 text-xs text-muted-foreground">{formatDateTime(proposal.created_at)} 제안</p>
      </SectionCard>
      <SectionCard title="찬반 남기기">
        {canVote(proposal) ? <VoteForm proposalId={proposal.id} /> : <p className="text-sm text-muted-foreground">{closed}</p>}
      </SectionCard>
      <SectionCard title={`의견 ${proposal.votes.length}`}>
        {proposal.votes.length === 0 ? (
          <EmptyState variant="plain">아직 의견이 없습니다.</EmptyState>
        ) : (
          <ul className="flex flex-col divide-y divide-border" data-testid="vote-list">
            {proposal.votes.map((vote) => (
              <li key={vote.id} className="flex flex-col gap-y-1 py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Pill tone={vote.stance === "pro" ? "primary" : "danger"}>{VOTE_LABEL[vote.stance]}</Pill>
                  <span className="font-medium">{vote.org_name}</span>
                  {vote.is_mine && <Pill>우리 회사</Pill>}
                  <span className="text-xs text-muted-foreground">{formatDateTime(vote.created_at)}</span>
                </div>
                <p className="whitespace-pre-line break-words text-sm text-muted-foreground">{vote.reason}</p>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
};

export default ProposalDetail;
